import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Appointment } from 'types/appointments';
import { useSubmitApi } from 'hooks/customHooks/authHook';
import { ApiCalls } from '../../../components/forms/providerAppointmentForms/AllAppointmentsApi';
import moment from 'moment';
import CorticaLoader from '../../../components/sharedComponents/CorticaLoader/CorticaLoader';
import Popup from '../../../components/sharedComponents/reactHookComponents/popup/popup';
import { CustomAppointmentWrapper } from '../../../components/forms/providerAppointmentForms/style';
import { useTheme, useMediaQuery, Box, Typography } from '@mui/material';
import { FormItem } from '../../../components/sharedComponents/reactHookComponents/formItem';

import './CollectTeacherSignature.scss';

const CollectTeacherSignature: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { submitApiCall } = useSubmitApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const appointment = location.state?.appointment as Appointment;

  const [isUnavailable, setIsUnavailable] = useState<boolean>(false);
  const [printName, setPrintName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  const signatureCanvasRef = useRef<SignatureCanvas>(null);
  const [hasSignature, setHasSignature] = useState<boolean>(false);
  const [savedSignatureData, setSavedSignatureData] = useState<string | null>(null);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState<boolean>(true);

  useEffect(() => {
    if (!appointment) {
      navigate('/providerappointments');
    }
  }, [appointment, navigate]);

  useEffect(() => {
    if (!isUnavailable && signatureCanvasRef.current && savedSignatureData) {
      try {
        signatureCanvasRef.current.fromDataURL(savedSignatureData);
        setHasSignature(true);
        setIsCanvasEmpty(false);
      } catch (error) {
        console.error('[CollectTeacherSignature] Error restoring signature:', error);
      }
    } else if (!isUnavailable && signatureCanvasRef.current) {
      const isEmpty = signatureCanvasRef.current.isEmpty();
      setIsCanvasEmpty(isEmpty);
      setHasSignature(!isEmpty);
    }
  }, [isUnavailable, savedSignatureData]);

  const handleBack = () => {
    navigate('/providerappointments', {
      state: {
        prefStartDate: appointment?.start,
      },
    });
  };

  const handleClearSignature = () => {
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.clear();
      setHasSignature(false);
      setSavedSignatureData(null);
      setIsCanvasEmpty(true);
    }
  };

  // A single tap or a tiny flick still reports as non-empty, so guard against
  // accidental "signatures". The floor is a single handwritten letter: require
  // enough sample points, a minimum bounding box, AND a minimum total pen travel
  // (path length). Path length is the strongest signal — even one deliberate
  // letter travels well past these values, while a tap/flick barely moves.
  const MIN_SIGNATURE_POINTS = 4;
  const MIN_SIGNATURE_SPAN_PX = 30;
  const MIN_SIGNATURE_PATH_PX = 70;

  const hasSubstantialSignature = (): boolean => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || canvas.isEmpty()) {
      return false;
    }

    const strokes = canvas.toData();
    if (!strokes || strokes.length === 0) {
      return false;
    }

    let totalPoints = 0;
    let pathLength = 0;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    strokes.forEach((stroke: any) => {
      // signature_pad v2 returns an array of point arrays (Point[][]); newer
      // versions return { points: Point[] }[]. Support both shapes.
      const points: Array<{ x: number; y: number }> = Array.isArray(stroke)
        ? stroke
        : stroke?.points || [];
      let prev: { x: number; y: number } | null = null;
      points.forEach((point) => {
        totalPoints += 1;
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
        if (prev) {
          pathLength += Math.hypot(point.x - prev.x, point.y - prev.y);
        }
        prev = point;
      });
    });

    if (totalPoints < MIN_SIGNATURE_POINTS) {
      return false;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const span = Math.sqrt(width * width + height * height);

    return span >= MIN_SIGNATURE_SPAN_PX && pathLength >= MIN_SIGNATURE_PATH_PX;
  };

  const handleSignatureEnd = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    setIsCanvasEmpty(canvas.isEmpty());

    if (hasSubstantialSignature()) {
      setHasSignature(true);
      setSavedSignatureData(canvas.toDataURL('image/png'));
    } else {
      setHasSignature(false);
      setSavedSignatureData(null);
    }
  };

  const handleSignatureBegin = () => {

    setIsCanvasEmpty(false);
  };

  const isFormValid = () => {
    if (isUnavailable) {
      return true;
    }
    return printName.trim().length > 0 && hasSignature;
  };

  const formatAppointmentDetails = () => {
    if (!appointment) return '';
    const startDate = moment(appointment.startAt);
    const endDate = moment(appointment.endAt);
    const formattedDate = startDate.format('dddd, MMMM D, YYYY');
    const formattedStartTime = startDate.format('hh:mm a');
    const formattedEndTime = endDate.format('hh:mm a');
    const clientName = appointment.client_name || 'N/A';

    return `Teacher/School Admin sign-off for ${clientName} on ${formattedDate} from ${formattedStartTime} - ${formattedEndTime}.`;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || !appointment) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let signatureBase64: string | null = null;

      if (!isUnavailable && signatureCanvasRef.current) {
        const canvas = signatureCanvasRef.current.getCanvas();
        signatureBase64 = canvas.toDataURL('image/png');
      }

      const payload = {
        eventId: appointment.id,
        isUnavailableStatus: isUnavailable,
        teacherFullName: isUnavailable ? null : printName.trim(),
        unavailableStatusNotes: isUnavailable ? (notes.trim() || null) : null,
        signatureBase64: signatureBase64,
      };

      await submitApiCall(ApiCalls.CollectTeacherSignature, payload);

      setIsSubmitting(false);
      setShowSuccessMessage(true);
      setTimeout(() => {
        navigate('/providerappointments', {
          state: {
            prefStartDate: appointment?.start,
          },
        });
      }, 3000);
    } catch (err: any) {
      console.error('[CollectTeacherSignature] Error submitting signature:', err);
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to submit signature. Please try again.';
      setError(errorMessage);
      setShowErrorPopup(true);
      setIsSubmitting(false);
    }
  };

  return (
    <CustomAppointmentWrapper className="collect-teacher-signature-page">
      {isSubmitting && !showSuccessMessage && <CorticaLoader message="Submitting signature..." />}

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        heading="Error"
        paragraph={error || 'An error occurred while submitting the signature.'}
      />

      <div className="form-enclosure max-span">
        {showSuccessMessage && (
          <div className="success-message-inline">
            <span>Signature collected successfully!</span>
          </div>
        )}
        <div className="main-form collect-teacher-signature-card">
          <div className="w-100 d-flex header-wrapper">
            <div className="header-div">
              <div className="back-to v-center">
                <button onClick={handleBack}>
                  <FontAwesomeIcon className="icon-back" icon={faChevronLeft} />
                </button>
              </div>
              <div className="page-desc v-center">
                <span>Collect Signature</span>
              </div>
            </div>
          </div>

          <Box className="collect-teacher-signature-copy">
            <Typography
              variant="body1"
              sx={{
                color: '#122f4e',
                fontSize: isMobile ? '14px' : '16px',
              }}
            >
              {formatAppointmentDetails()}
            </Typography>
          </Box>

          <div className="form-row collect-row">
            <FormItem optional={false} label="">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isUnavailable}
                  onChange={(e) => setIsUnavailable(e.target.checked)}
                  className="checkbox-input"
                />
                <span>Teacher/School Admin Unavailable?</span>
              </label>
            </FormItem>
          </div>

          {!isUnavailable && (
            <>
              <div className="form-row collect-row">
                <FormItem optional={false} label="">
                  <input
                    type="text"
                    value={printName}
                    onChange={(e) => setPrintName(e.target.value)}
                    placeholder="Print Name"
                    className="text-input"
                  />
                </FormItem>
              </div>

              <div className="form-row collect-row">
                <FormItem optional={false} label="">
                  <div className="signature-container">
                    <SignatureCanvas
                      ref={signatureCanvasRef}
                      canvasProps={{
                        className: 'signature-canvas',
                      }}
                      onEnd={handleSignatureEnd}
                      onBegin={handleSignatureBegin}
                    />
                    {isCanvasEmpty && (
                      <span className="signature-placeholder">
                        Draw Signature Here
                      </span>
                    )}
                    {!isCanvasEmpty && (
                      <button
                        className="clear-signature-button"
                        onClick={handleClearSignature}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        <span>Clear</span>
                      </button>
                    )}
                  </div>
                </FormItem>
              </div>
            </>
          )}

          {isUnavailable && (
            <div className="form-row collect-row">
              <FormItem optional={true} label="(Optional) Notes">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add appointment notes"
                  className="notes-textarea"
                  rows={4}
                />
              </FormItem>
            </div>
          )}
          <div className="submit-button collect-submit">
            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </CustomAppointmentWrapper>
  );
};

export default CollectTeacherSignature;
